import {
  AccountAmountField,
  AccountAmountValue,
  Box,
  BrightyStepButton,
  Button,
  ButtonLink,
  ContentIsland,
  FormContentIsland,
  GraphicsPumpkinImage,
  Grid,
  IconButton,
  InvertIcon,
  Stack,
  Step,
  Stepper,
  Tooltip,
  TransferBreakdown,
  Typography,
  UiFee,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '../../Layout/MainLayout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccountsWithAccountQuery } from '../../../api/accounts';
import { useOwnTransferIntentQuery, useCreateOwnTransferMutation } from '../../../api/transfers';
import { ApiAccount, ApiOwnTransferIntentRequest } from '../../../openapi';
import {
  mapClientMoneyToServerMoney,
  parseFloatValue,
  serverAmountToClientAmount,
} from '@razz-team/utils';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { TransferComplete } from '../../TransferComplete/TransferComplete';
import { Form } from '@razz-team/ui-web/src/components/Form/Form';
import { Route } from '../../../routes/account.exchange';
import { getExchangeAccounts } from './utils';

export function ExchangePage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const accountsQuery = useAccountsWithAccountQuery(search.accountId);

  const [intentRequest, setIntentRequest] = useState<ApiOwnTransferIntentRequest | undefined>();
  const intentQuery = useOwnTransferIntentQuery(intentRequest);
  const idempotencyKey = useIdempotencyKey();
  const transferMutation = useCreateOwnTransferMutation({ idempotencyKey });

  const [directions, setAccounts] = useState<{
    from: AccountAmountValue<ApiAccount> | undefined;
    to: AccountAmountValue<ApiAccount> | undefined;
  }>(() => ({
    from: undefined,
    to: undefined,
  }));

  const options = useMemo(() => accountsQuery.data ?? [], [accountsQuery.data]);

  const noAccountsToExchange = Boolean(
    accountsQuery.data?.length && accountsQuery.data?.length < 2,
  );

  useEffect(() => {
    setAccounts((prev) => {
      if ((prev.from && prev.to) || options.length < 2) return prev;
      const { from, to } = getExchangeAccounts({
        defaultAccountId: search.accountId,
        accounts: options,
      });
      return {
        from: from ? { clientAmount: '', account: from } : undefined,
        to: to ? { clientAmount: '', account: to } : undefined,
      };
    });
  }, [options, search.accountId]);

  const handleChangeFrom = useCallback((nextFrom: AccountAmountValue<ApiAccount>) => {
    setAccounts((prev) => {
      const next = {
        from: nextFrom,
        to: prev.to?.account.id === nextFrom.account.id ? prev.from : prev.to,
      };

      if (next.to) {
        const amount = parseFloatValue(next.from.clientAmount);
        setIntentRequest(
          amount === 0
            ? undefined
            : {
                side: 'SELL',
                amount: mapClientMoneyToServerMoney({
                  amount,
                  currency: next.from.account.balance.currency,
                }),
                sourceCurrency: next.from.account.balance.currency,
                targetCurrency: next.to.account.balance.currency,
              },
        );
      }

      return next;
    });
  }, []);

  const handleChangeTo = useCallback((nextTo: AccountAmountValue<ApiAccount>) => {
    setAccounts((prev) => {
      const next = {
        to: nextTo,
        from: prev.from?.account.id === nextTo.account.id ? prev.to : prev.from,
      };

      if (next.from) {
        const amount = parseFloatValue(next.to.clientAmount);

        setIntentRequest(
          amount === 0
            ? undefined
            : {
                side: 'BUY',
                amount: mapClientMoneyToServerMoney({
                  amount: parseFloatValue(next.to.clientAmount),
                  currency: next.to.account.balance.currency,
                }),
                sourceCurrency: next.from.account.balance.currency,
                targetCurrency: next.to.account.balance.currency,
              },
        );
      }

      return next;
    });
  }, []);

  const invertAccounts = useCallback(() => {
    if (directions.from) {
      handleChangeTo(directions.from);
    }
  }, [directions.from, handleChangeTo]);

  useEffect(() => {
    const intent = intentQuery.data;
    if (intent) {
      if (intent.quote.fx) {
        const {
          amount,
          quote: { targetAmount },
        } = intent;
        const fx = intent.quote.fx;
        setAccounts((prev) => {
          if (fx.side === 'SELL' && prev.from && prev.to) {
            return {
              from: prev.from,
              to: {
                account: prev.to.account,
                clientAmount: String(serverAmountToClientAmount(targetAmount)),
              },
            };
          }

          if (fx.side === 'BUY' && prev.from && prev.to) {
            return {
              to: prev.to,
              from: {
                account: prev.from.account,
                clientAmount: String(serverAmountToClientAmount(amount)),
              },
            };
          }

          return prev;
        });
      } else {
        // support sending between accounts with the same currency
        setAccounts((prev) => {
          const { to, from } = prev;
          if (
            to &&
            from &&
            to.account.balance.currency === from.account.balance.currency &&
            to.account.balance.currency === intent.quote.targetAmount.currency &&
            from.account.balance.currency === intent.quote.sourceAmount.currency
          ) {
            return {
              from: {
                account: from.account,
                clientAmount: String(serverAmountToClientAmount(intent.quote.sourceAmount)),
              },
              to: {
                account: to.account,
                clientAmount: String(serverAmountToClientAmount(intent.quote.targetAmount)),
              },
            };
          }

          return prev;
        });
      }
    }
  }, [intentQuery.data]);

  const [step, setStep] = useState<'amount' | 'confirm'>('amount');
  const backToEdit = useCallback(() => setStep('amount'), []);

  const handleSubmit = useCallback(() => {
    if (step === 'amount') {
      setStep('confirm');
    } else if (transferMutation.isIdle && intentQuery.data && directions.from && directions.to) {
      transferMutation.mutate({
        hash: intentQuery.data.hash,
        fees: intentQuery.data.fees,
        quote: intentQuery.data.quote,
        sourceAccountId: directions.from?.account.id,
        targetAccountId: directions.to?.account.id,
      });
    }
  }, [step, directions, intentQuery.data, transferMutation]);

  useEffect(() => {
    if (transferMutation.isSuccess) {
      setStep('confirm');
    }
  }, [transferMutation.isSuccess]);

  const uiFees = useMemo(() => {
    return intentQuery.data?.fees
      ? Object.entries(intentQuery.data?.fees ?? {}).reduce((acc, [type, fee]) => {
          if (fee?.sourceAmount) {
            acc.push({
              type,
              value: fee.sourceAmount,
            });
          }
          return acc;
        }, [] as UiFee[])
      : null;
  }, [intentQuery.data?.fees]);

  const uiRate = useMemo(() => {
    if (intentQuery.data) {
      const { fx } = intentQuery.data.quote;
      if (fx) {
        return {
          price: fx.side === 'BUY' ? fx.rate.ask : fx.rate.bid,
          currencyPair: fx.rate.currencyPair,
        };
      }

      return undefined;
    }

    return null;
  }, [intentQuery.data]);

  const fromTitle = useMemo(() => {
    const account = directions.from?.account;
    if (!account) return t('From');

    const accountName =
      account.name ?? t('Account {{currency}}', { currency: account.balance.currency });
    return t('From {{accountName}}', { accountName });
  }, [directions.from?.account, t]);

  const toTitle = useMemo(() => {
    const account = directions.to?.account;
    if (!account) return t('To');

    const accountName =
      account.name ?? t('Account {{currency}}', { currency: account.balance.currency });
    return t('To {{accountName}}', { accountName });
  }, [directions.to?.account, t]);

  if (noAccountsToExchange) {
    return (
      <MainLayout title={t('Exchange')} compact>
        <Grid size={12}>
          <ContentIsland sx={{ padding: 6 }}>
            <Stack direction="row" spacing={4}>
              {__BRIGHTY__ ? <GraphicsPumpkinImage width={120} height={120} /> : null}
              <Stack justifyContent="center">
                <Typography variant="h6">{t('No accounts to exchange')}</Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('Open one more account to exchange money between them')}
                </Typography>
              </Stack>
            </Stack>
          </ContentIsland>
        </Grid>
        <Grid size={12}>
          <Button href="/account/accounts/create" color="primary" fullWidth>
            {t('Open new account')}
          </Button>
        </Grid>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={t('Exchange')} compact>
      <Grid size={12}>
        <Box sx={{ width: '100%' }}>
          <Stepper alternativeLabel activeStep={step === 'confirm' ? 1 : 0}>
            <Step active={step === 'amount'}>
              <BrightyStepButton onClick={backToEdit}>{t('Amount')}</BrightyStepButton>
            </Step>
            <Step active={step === 'confirm'}>
              <BrightyStepButton>{t('Confirm')}</BrightyStepButton>
            </Step>
          </Stepper>
        </Box>
      </Grid>
      <Grid
        component={Form}
        container
        display="flex"
        justifyContent="center"
        onSubmit={handleSubmit}
        size={12}
      >
        <Stack direction="column" width="100%" maxWidth={500} spacing={6} alignItems="stretch">
          {transferMutation.data?.transaction ? (
            <TransferComplete transaction={transferMutation.data?.transaction} />
          ) : (
            <>
              {step === 'amount' ? (
                <FormContentIsland title={fromTitle}>
                  <AccountAmountField
                    value={directions.from}
                    options={options}
                    onChange={handleChangeFrom}
                    autoFocus
                    helperSellAll
                    validateBalance
                  />
                </FormContentIsland>
              ) : null}

              <ContentIsland
                title={step === 'confirm' ? t('Exchange details') : undefined}
                actions={
                  step === 'confirm' ? (
                    <Button variant="text" color="info" onClick={backToEdit}>
                      {t('Edit')}
                    </Button>
                  ) : undefined
                }
              >
                <TransferBreakdown
                  variant={step === 'confirm' ? 'confirm' : 'form'}
                  side={intentQuery.data?.quote.fx?.side}
                  amountToDebit={step === 'confirm' ? intentQuery.data?.amount : undefined}
                  amountToConvert={intentQuery.data?.quote.sourceAmount ?? null}
                  fees={uiFees}
                  rate={uiRate}
                  amountToTransfer={
                    step === 'confirm' ? intentQuery.data?.quote.targetAmount : undefined
                  }
                  error={transferMutation.error ?? intentQuery.error}
                />
              </ContentIsland>
              {step === 'amount' ? (
                <FormContentIsland
                  title={toTitle}
                  actions={
                    <Tooltip title={t('Invert from and to accounts')}>
                      <IconButton size="small" onClick={invertAccounts}>
                        <InvertIcon />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <AccountAmountField
                    value={directions.to}
                    options={options}
                    onChange={handleChangeTo}
                  />
                </FormContentIsland>
              ) : null}
            </>
          )}
          {transferMutation.data?.transaction ? (
            <ButtonLink
              size="large"
              color="primary"
              to="/account/$accountId"
              params={{ accountId: transferMutation.variables.targetAccountId }}
              fullWidth
            >
              {t('Done')}
            </ButtonLink>
          ) : (
            <Button
              type="submit"
              size="large"
              color="primary"
              loading={
                (intentQuery.isEnabled && intentQuery.isPending) || transferMutation.isPending
              }
              disabled={!intentQuery.isSuccess}
              fullWidth
            >
              {step === 'confirm' ? t('Confirm') : t('Review order')}
            </Button>
          )}
        </Stack>
      </Grid>
    </MainLayout>
  );
}
